/**
 * Local-first sync rules (last-write-wins with soft deletes).
 *
 * Every synced record carries:
 *  - `id`: a UUIDv7 generated on the device, so records can be created offline.
 *  - `updatedAt`: client clock, decides conflicts (clamped to server time on push).
 *  - `deletedAt`: soft-delete marker so deletions sync too.
 * The server also stamps `syncedAt`, which is what pull cursors use, so a device with a
 * wrong clock can never miss changes.
 */

export interface SyncRecord {
  id: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** True when `incoming` should replace `current`. Newer wins; on a tie a delete wins. */
export function incomingWins(current: SyncRecord | undefined, incoming: SyncRecord): boolean {
  if (!current) return true;
  const a = Date.parse(current.updatedAt);
  const b = Date.parse(incoming.updatedAt);
  if (b !== a) return b > a;
  return incoming.deletedAt !== null && current.deletedAt === null;
}

/** Merge incoming records into local ones. Returns the merged list and the ids that changed. */
export function mergeRecords<T extends SyncRecord>(
  local: T[],
  incoming: T[],
): { records: T[]; changedIds: string[] } {
  const byId = new Map(local.map((r) => [r.id, r]));
  const changedIds: string[] = [];
  for (const rec of incoming) {
    if (incomingWins(byId.get(rec.id), rec)) {
      byId.set(rec.id, rec);
      changedIds.push(rec.id);
    }
  }
  return { records: [...byId.values()], changedIds };
}

/** Records edited locally since the last successful push. */
export function pendingChanges<T extends SyncRecord>(
  records: T[],
  lastPushedAt: string | null,
): T[] {
  if (!lastPushedAt) return [...records];
  const t = Date.parse(lastPushedAt);
  return records.filter((r) => Date.parse(r.updatedAt) > t);
}

/** Never let a device claim a time in the future; clamp to server `now`. */
export function clampToNow<T extends SyncRecord>(record: T, now: Date): T {
  const n = now.getTime();
  const clamp = (iso: string | null) => (iso && Date.parse(iso) > n ? now.toISOString() : iso);
  return { ...record, updatedAt: clamp(record.updatedAt)!, deletedAt: clamp(record.deletedAt) };
}

/** Records with a deletedAt are hidden from the UI but kept until synced. */
export function visible<T extends SyncRecord>(records: T[]): T[] {
  return records.filter((r) => r.deletedAt === null);
}

/** Mark a record deleted (returns a new object). */
export function softDelete<T extends SyncRecord>(record: T, now: Date): T {
  const iso = now.toISOString();
  return { ...record, updatedAt: iso, deletedAt: iso };
}

/** Stamp an edit (returns a new object). */
export function touch<T extends SyncRecord>(record: T, now: Date): T {
  return { ...record, updatedAt: now.toISOString() };
}
